// Customizable Area Start
import React, { useState, useRef } from "react";
import { createStyles, Theme } from "@material-ui/core/styles";
import { Box, IconButton, Typography, withStyles } from "@material-ui/core";
import { NormalizedFile, File } from "./types";
import { AddRounded, Close } from "@material-ui/icons";
import { Swiper, SwiperSlide } from 'swiper/react';

type Props = {
  classes: any;
  videos: string;
  files: NormalizedFile[];
  selectFile: (fileIndex: number) => void;
  addFileButtonRef: React.RefObject<HTMLInputElement>;
  upload: string;
  addFiles: (file: File[]) => void;
  isEditMode: boolean;
  removeFile: (fileIndex: number) => void;
  reorderFiles: (fromIndex: number, toIndex: number) => void;
};

const styles = (theme: Theme) =>
  createStyles({
    postFilesQueue: {
      display: "flex",
      flexDirection: "column",
      paddingTop: "10px",
      gap: "10px",
      flexWrap: "wrap",
      borderTop: "1px solid #AEAEB2"
    },
    filesQueueTitle: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeightStep: "32px",
      letterSpacing: "-0.12px",
    },
    swiper:{
      width: "500px",
      marginLeft: "0px",
      [theme.breakpoints.down(650)]: {
        width: "300px"
      },
    },
    fileItem: {
      position: "relative",
      width: "100px !important",
      height: "100px",
      touchAction: "none", // Stops mobile browser window from bouncing or scrolling
      userSelect: "none",
      "&:hover": {
        cursor: "pointer",
      },
    },
    fileItemDragging: {
      opacity: 0.4,
      transform: "scale(1.1)", 
      zIndex: 10,
    },
    fileItemThumbnail: {
      position: "relative",
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "4px solid #FFC629",
      overflow: "hidden",
      pointerEvents: "none",
      "& > *": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
    },
    deleteFileButton: {
      position: "absolute",
      top: "2px",
      right: "2px",
      width: "24px",
      height: "24px",
      padding: 0,
      backgroundColor: "#0F172A",
      color: "#FFF",
      zIndex: 12,
      "&:hover": {
        backgroundColor: "#000",
      },
      "& svg": {
        width: "16px",
        height: "16px",
      },
    },
    thumbnailShade: {
      position: "absolute",
      top: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0,0,0,0.4)",
    },
    fileItemTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: "16px",
      fontWeight: 700,
      color: "#FFF",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "center",
    },
    addFileButton: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100px",
      height: "100px",
      borderRadius: "50%",
      backgroundColor: "#FFC629",
      cursor: "pointer",
      gap: "2px",
      "&:hover": {
        backgroundColor: "#eebe40",
      }
    },
    addFileText: {
      fontSize: "16px",
      fontWeight: 700,
    },
    iconContainer: {
      width: "25px",
      height: "25px",
      borderRadius: "9px",
      backgroundColor: "#FFF",
    },
  });

export const CreateEveryreelSettingsFilesQueue = ({
  classes,
  files,
  videos,
  selectFile,
  addFiles,
  addFileButtonRef,
  upload,
  isEditMode,
  removeFile,
  reorderFiles,
}: Props) => {
  const [activeDragIndex, setActiveDragIndex] = useState<number | null>(null);
  const currentDragIndexRef = useRef<number | null>(null);

  const handlePointerDown = (e: React.PointerEvent, index: number) => {
    if (!isEditMode) return;
    
    // Capture pointer targeting directly onto the element so tracking persists smoothly
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    setActiveDragIndex(index);
    currentDragIndexRef.current = index;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isEditMode || currentDragIndexRef.current === null) return;

    const pointerX = e.clientX;

    // Loop through all items to calculate which bounding box our pointer is inside
    for (let i = 0; i < files.length; i++) {
      if (i === currentDragIndexRef.current) continue;

      const element = document.getElementById(`fileItem-${i}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        
        // Check if the current touch point is within the horizontal bounds of another bubble card
        if (pointerX >= rect.left && pointerX <= rect.right) {
          reorderFiles(currentDragIndexRef.current, i);
          currentDragIndexRef.current = i;
          setActiveDragIndex(i);
          break;
        }
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch (err) {
      // Fallback fallback if element was deleted mid-drag
    }
    setActiveDragIndex(null);
    currentDragIndexRef.current = null;
  };

  return (
    <Box className={classes.postFilesQueue}>
      <Typography className={classes.filesQueueTitle}>{videos}</Typography>
      <Swiper
        spaceBetween={20}
        slidesPerView={"auto"}
        centeredSlides={false}
        grabCursor={!isEditMode}
        slideToClickedSlide={!isEditMode}
        resistanceRatio={0.85}
        cssMode={false}
        speed={300}
        allowTouchMove={!isEditMode}
        className={classes.swiper}
      >
        {files.map((file, i) => (
          <SwiperSlide
            id={`fileItem-${i}`}
            key={file.id || file.meta.preview}
            className={`${classes.fileItem} ${activeDragIndex === i ? classes.fileItemDragging : ""}`}
            onClick={() => {
              if (!isEditMode) selectFile(i);
            }}
            // Combined modern pointer tracker
            onPointerDown={(e:any) => handlePointerDown(e, i)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <Box
              className={classes.fileItemThumbnail}
              style={{
                border: file.selected ? "4px solid #FFC629" : "none",
              }}
            >
              {file.type.includes("image") ? (
                <img src={file.meta.preview} alt="image preview" />
              ) : (
                <video muted playsInline>
                  <source src={file.meta.preview} type="video/mp4" />
                </video>
              )}
              <Box className={classes.thumbnailShade}></Box>
              <Typography className={classes.fileItemTitle}>
                {file?.title}
              </Typography>
            </Box>
            {isEditMode && (
              <IconButton
                className={classes.deleteFileButton}
                size="small"
                data-test-id={`delete-file-${i}`}
                onPointerDown={(e) => e.stopPropagation()} // Stop delete click from registering as a drag coordinate
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(i);
                }}
              >
                <Close />
              </IconButton>
            )}
          </SwiperSlide>
        ))}

        {/* + Add Slide */}
        {files.length < 15 && (
          <SwiperSlide className={classes.fileItem}>
            <Box
              id="addFileButton"
              className={classes.addFileButton}
              onClick={() => addFileButtonRef.current?.click()}
            >
              <Box className={classes.iconContainer}>
                <AddRounded />
              </Box>
              <Typography className={classes.addFileText}>{upload}</Typography>
              <input
                hidden
                ref={addFileButtonRef}
                type="file"
                data-test-id="media-upload"
                accept="video/mp4"
                onChange={(e) => {
                  if (e.currentTarget.files) {
                    const file = e.currentTarget.files[0];
                    const formattedFile = Object.assign(file, {
                      preview: URL.createObjectURL(file),
                    });
                    addFiles([formattedFile]);
                  }
                }}
              />
            </Box>
          </SwiperSlide>
        )}
      </Swiper>
    </Box>
  );
};

export default withStyles(styles)(CreateEveryreelSettingsFilesQueue);
// Customizable Area End